import { useEffect, useState } from 'react';
import { Card, Flex, Steps, theme, Typography } from 'antd'
import { SUCCESS_COLOR } from '@shared/constants/colors';
import { StepCompletedIcon } from '@/features/agreements/icons/StepCompletedIcon';
import { StepNavigation } from './StepNavigation';

const { Text } = Typography;

/**
 * Renderiza un stepper con navegación entre pasos. 
 * 
 * Reglas de navegación:
 * - Si la función `onNext` / `onPrev`:
 *   - retorna `false` → se bloquea la navegación.
 *   - lanza una excepción → se bloquea la navegación.
 *   - retorna cualquier otro valor (`true`, `null`, `undefined`) → la navegación continúa.
 * 
 * @param {object} props 
 * @param {Array<{
 *   key: string,
 *   icon?: React.ReactNode,
 *   title?: string,
 *   children?: React.ReactNode,
 *   onNext?: () => Promise<boolean | void> | boolean | void,
 *   onPrev?: () => Promise<boolean | void> | boolean | void,
 * }>} props.items Configuración de los pasos del stepper.
 * @param {number} [props.initialStep=0] Índice del paso inicial.
 * @param {number} [props.step] Paso actual (modo controlado).
 * @param {function} [props.onStepChange] Callback al cambiar el paso (modo controlado).
 * @param {string} [props.lastStepNextLabel] Texto del botón "siguiente" en el último paso (ej. "Activar convenio").
 */
export const Stepper = ({ items, initialStep = 0, step: controlledStep, onStepChange, lastStepNextLabel }) => {
  const { token: themeToken } = theme.useToken();
  const [internalStep, setInternalStep] = useState(initialStep);
  const [maxStepReached, setMaxStepReached] = useState(initialStep);
  const [navigationLoading, setNavigationLoading] = useState(null); // 'next' | 'prev'

  const isControlled = controlledStep !== undefined && typeof onStepChange === 'function';
  const currentStep = isControlled ? controlledStep : internalStep;
  const setCurrentStep = isControlled ? onStepChange : setInternalStep;

  useEffect(() => {
    if (isControlled) {
      setMaxStepReached((prev) => Math.max(prev, controlledStep));
    }
  }, [isControlled, controlledStep]);

  const currentItem = items[currentStep];

  const handleNext = async () => {
    try {
      if (currentItem?.onNext) {
        setNavigationLoading('next');
        const result = await currentItem.onNext();
        if (result === false) return;
      }
      const nextStep = Math.min(currentStep + 1, items.length - 1);
      setMaxStepReached((prev) => Math.max(prev, nextStep));
      setCurrentStep(nextStep);
    } catch {
      // El step maneja y expone sus propios errores
    } finally {
      setNavigationLoading(null);
    }
  };

  const handlePrev = async () => {
    try {
      if (currentItem?.onPrev) {
        setNavigationLoading('prev');
        const result = await currentItem.onPrev();
        if (result === false) return;
      }
      setCurrentStep(Math.max(currentStep - 1, 0));
    } catch {
      // El step maneja y expone sus propios errores
    } finally {
      setNavigationLoading(null);
    }
  };

  const finalItems = items.map(({ icon, title }, step) => {
    const isActive = currentStep === step;
    const isCompleted = currentStep > step;

    const finalIcon = (
      isActive ? <span style={{ color: themeToken.colorPrimary }}>{icon}</span> :
        isCompleted ? <StepCompletedIcon style={{ color: SUCCESS_COLOR }} /> :
          icon
    );

    const titleColor = (
      isActive ? themeToken.colorPrimary :
        isCompleted ? SUCCESS_COLOR :
          'inherit'
    );

    return {
      icon: finalIcon,
      title: <Text strong style={{ color: titleColor }}>{title}</Text>,
    };
  });

  return (
    <Flex vertical gap={32}>
      <Steps
        items={finalItems}
        current={currentStep}
      />
      {items.map(({ key, children }, index) => {
        if (index > maxStepReached) return null; // Solo se renderizan los pasos alcanzados

        return (
          <Card
            key={key}
            style={{
              backgroundColor: '#fafafa',
              display: index === currentStep ? 'block' : 'none', // Solo se muestra el paso actual
            }}
          >
            {children}
          </Card>
        )
      })}
      <StepNavigation
        currentStep={currentStep}
        totalSteps={items.length}
        onNext={handleNext}
        onPrev={handlePrev}
        navigationLoading={navigationLoading}
        lastStepNextLabel={lastStepNextLabel}
      />
    </Flex>
  );
};